import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/UserOrders.css";
import { FaBoxOpen } from "react-icons/fa";

/**
 * MyOrder - shows grouped orders
 * Expects backend response like: { orders: [ { orderId, productImage, productName, productDescription,
 *    quantity, size, totalPrice, orderDate, deliveryDate, deliveryStatus, deliveryPersonName,
 *    deliveryPersonContact } , ... ] }
 *
 * Groups by orderId and renders:
 *  - order header (order id, item count, ordered date, status badge, optional delivery person)
 *  - product cards for each item in that order
 */
const MyOrder = () => {
  const user = JSON.parse(sessionStorage.getItem("active-user"));
  const [rawOrders, setRawOrders] = useState([]); // flat list from backend
  const [groupedOrders, setGroupedOrders] = useState([]);

  useEffect(() => {
    const getMyOrder = async () => {
      try {
        const response = await axios.get(
          "https://goldenhides.in/api/user/myorder?userId=" + user.id
        );
        const data = response.data || {};
        // assume backend returns { orders: [...] } or if top-level array use response.data
        const orders = data.orders ?? data;
        setRawOrders(Array.isArray(orders) ? orders : []);
      } catch (err) {
        console.error("Failed to load orders", err);
      }
    };

    getMyOrder();
  }, [user?.id]);

  // group rawOrders by orderId
  useEffect(() => {
    if (!rawOrders || rawOrders.length === 0) {
      setGroupedOrders([]);
      return;
    }

    const map = new Map();
    rawOrders.forEach((o) => {
      const id = o.orderId || `order_${o.orderId}`; // fallback
      if (!map.has(id)) {
        map.set(id, {
          orderId: id,
          orderDate: o.orderDate,
          deliveryStatus: o.deliveryStatus,
          deliveryPersonName: o.deliveryPersonName,
          deliveryPersonContact: o.deliveryPersonContact,
          items: [],
        });
      }
      // push item
      map.get(id).items.push({
        productId: o.productId,
        productName: o.productName,
        productDescription: o.productDescription,
        productImage: o.productImage,
        quantity: o.quantity,
        size: o.size,
        totalPrice: o.totalPrice,
      });
    });

    // create array preserving insertion order
    const groups = Array.from(map.values());
    setGroupedOrders(groups);
  }, [rawOrders]);

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "";
    const d = new Date(Number(epochTime));
    // e.g. "12 Oct 2025, 03:28 pm"
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, opts);
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  };

  if (!user) {
    return (
      <div className="mt-3">
        <div className="card form-card ms-2 me-2 mb-5 custom-bg">
          <div className="card-body">
            <p>Please login to see your orders.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 px-2">
      <div className="container-fluid">
        <div className="text-center mb-4">
          <h2 className="fw-bold text-success">
            <FaBoxOpen className="me-2" />
            My Orders
          </h2>
          <p className="text-muted">Recent orders & their items</p>
        </div>

        {groupedOrders.length === 0 ? (
          <div className="card form-card ms-2 me-2 mb-5 custom-bg">
            <div className="card-body text-center">
              <p className="mb-0">No orders found.</p>
            </div>
          </div>
        ) : (
          groupedOrders.map((order) => {
            // compute total for this order (sum of item totals)
            const orderTotal = order.items.reduce(
              (acc, it) => acc + Number(it.totalPrice || 0),
              0
            );

            // deliveryPersonName/contact may be 'Pending' — hide if both are 'Pending' or falsy
            const showDeliveryInfo =
              order.deliveryPersonName &&
              order.deliveryPersonContact &&
              order.deliveryPersonName !== "Pending" &&
              order.deliveryPersonContact !== "Pending";

            return (
              <div
                key={order.orderId}
                className="card form-card ms-2 me-2 mb-4 custom-bg orders-group-card"
              >
                {/* Order header */}
                <div className="card-header d-flex align-items-center justify-content-between bg-white">
                  <div className="d-flex align-items-center gap-4">
                    <div>
                      <div className="small text-muted">Order ID</div>
                      <div style={{ fontWeight: 700 }}>#{order.orderId}</div>
                    </div>

                    <div>
                      <div className="small text-muted">Items</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.items.length} item
                        {order.items.length > 1 ? "s" : ""}
                      </div>
                    </div>

                    <div>
                      <div className="small text-muted">Ordered</div>
                      <div style={{ fontWeight: 600 }}>
                        {formatDateFromEpoch(order.orderDate)}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    {showDeliveryInfo && (
                      <div className="text-end small">
                        <div>
                          <strong>{order.deliveryPersonName}</strong>
                        </div>
                        <div className="small">
                          {order.deliveryPersonContact}
                        </div>
                      </div>
                    )}

                    <div>
                      <span
                        className={`badge order-status-badge ${
                          (order.deliveryStatus || "").toLowerCase() ===
                          "pending"
                            ? "badge-pending"
                            : "badge-other"
                        }`}
                      >
                        {order.deliveryStatus || "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Body: product cards */}
                <div className="card-body">
                  <div className="row g-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="col-12 col-md-4">
                        <div className="order-product-card p-3 h-100 shadow-sm rounded">
                          <div className="d-flex gap-3">
                            <div style={{ width: 90, height: 90 }}>
                              {item.productImage ? (
                                <img
                                  src={`https://goldenhides.in/api/product/${item.productImage}`}
                                  alt={item.productName}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    background: "#f1f1f1",
                                    borderRadius: 6,
                                  }}
                                />
                              )}
                            </div>

                            <div className="flex-grow-1">
                              <div style={{ fontWeight: 700 }}>
                                {item.productName}
                              </div>
                              <div className="small text-muted mt-1">
                                {item.productDescription
                                  ? item.productDescription.slice(0, 80) +
                                    (item.productDescription.length > 80
                                      ? "..."
                                      : "")
                                  : ""}
                              </div>

                              <div className="mt-2 small text-muted">
                                Qty: {item.quantity} &nbsp;|&nbsp; Size:{" "}
                                {item.size || "-"}
                              </div>
                            </div>

                            <div style={{ textAlign: "right", minWidth: 90 }}>
                              <div
                                className="text-success"
                                style={{ fontWeight: 700 }}
                              >
                                ₹ {Number(item.totalPrice || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: order total */}
                <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    Orders are processed securely
                  </div>
                  <div
                    style={{ fontWeight: 700, color: "#0a7a3b", fontSize: 18 }}
                  >
                    Total ₹ {orderTotal.toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyOrder;
